import React, { useEffect, useState, memo } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import "./DraggableGroupStyle.css";
import DeleteModal from "./DeleteModal";
import VariantStyles from "./VariantStyles";
import apiService from "../../services/ApiService";
import FillNameForm from "./FillNameForm";
import TreeItemActions from "./TreeItemActions";
import SnackBar from "./SnackBar";
import { cn } from "../../lib/utils";
import { useEditorStore } from "../../shared/stores/useEditorStore";
import { useScenarioTree } from "../../shared/hooks/queries/useScenarioTree";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../shared/hooks/queries/useSectionLoader";
import { useNodeExpand } from "./hooks/useNodeExpand";
import { removeElemFromTree, getItemStyle } from "./utils/treeUtils";
import { shouldBeConsidered, parentConsidered, isModifiedInSensitivity } from "./utils/sensitivityUtils";
import { isMatchSearchString, hasComputeErrors } from "./utils/searchUtils";
import type { TreeItem, Variant, SearchStrings, SnackBarContent, ScenarioState, Sensitivity } from "./types";

interface DraggableDataGroupProps {
  item: TreeItem;
  currentSensitivity: Sensitivity | null;
  variant: Variant;
  level: number;
  scenario_id: number;
  showDiffToggled: boolean;
  searchStrings: SearchStrings;
  selected: TreeItem | null;
  setSelected: (item: TreeItem | null) => void;
  renderInlineDetails?: (item: TreeItem) => React.ReactNode;
  sensitivityModifiedIds?: Set<string>;
  // Optional section-scoped filter: hide children whose type is not in the set.
  // null/undefined keeps every child visible (legacy behaviour).
  allowedTypes?: Set<string> | null;
}

function FDraggableDataGroup({
  item, currentSensitivity, variant, level, scenario_id, showDiffToggled,
  searchStrings, selected, setSelected, renderInlineDetails, sensitivityModifiedIds,
  allowedTypes,
}: DraggableDataGroupProps) {
  const scenarioId = useEditorStore((s) => s.scenarioId) ?? 0;
  const { state: currentState, patchTree } = useScenarioTree(scenarioId);
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY
    // (its queryFn returns empty initial state, so invalidation wipes the lazily-loaded tree)
    queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
  };

  const reloadState = (forceInvalidate?: boolean) => {
    patchTree((prev: ScenarioState) => {
      const next = { ...prev };
      if (next.Input?.tree) {
        next.Input = { ...next.Input, tree: { ...next.Input.tree, items: { ...next.Input.tree.items } } };
      }
      if (next.Output?.tree) {
        next.Output = { ...next.Output, tree: { ...next.Output.tree, items: { ...next.Output.tree.items } } };
      }
      return next;
    });
    if (forceInvalidate) invalidateQueries();
  };

  const userRights = useEditorStore((s) => s.userRights);
  const expandedNodes = useEditorStore((s) => s.expandedNodes);
  const loadingNodes = useEditorStore((s) => s.loadingNodes);
  const nodeKey = item.kind + item.id.toString();
  const isNodeExpanded = searchStrings[variant].length ? true : expandedNodes.has(nodeKey);

  const [editing, setEditing] = useState(item.editing);
  const [contextSnackBarContent, setContextSnackBarContent] = useState<SnackBarContent>({
    open: false, severity: "error", message: "",
  });

  const tree = currentState[variant].tree;
  const hasSensitivityModified = item.sensitivityModelData != null && Object.keys(item.sensitivityModelData.fields).length !== 0;
  const computeErrors = hasComputeErrors(item, tree);
  // The section-scoped allowlist is only meaningful at the `company` level —
  // that is where sibling subtrees (Asset/Equity/Bank Accounts/Corporate
  // Taxation) need to be hidden from one another. Inside a given subtree all
  // descendants are already legitimate, so we do NOT propagate `allowedTypes`
  // to child DraggableDataGroups (see recursion below).
  const isCompany = (item.type || '').toLowerCase() === 'company';
  const effectiveAllowedTypes = isCompany ? allowedTypes : null;
  const visibleChildrens = effectiveAllowedTypes
    ? item.childrens.filter((id) => {
        const child = tree.items[id];
        return child ? effectiveAllowedTypes.has((child.type || child.kind || '').toLowerCase()) : true;
      })
    : item.childrens;
  const isExpanded = (visibleChildrens.length || loadingNodes.has(nodeKey)) ? isNodeExpanded : false;
  const backgroundColor = hasSensitivityModified ? "#ffefbf" : VariantStyles[variant]["bgcolor"][level];

  // If the node is flagged expanded but its children aren't actually in the tree
  // (e.g. the user expanded it, navigated away, came back and the branch wasn't
  // re-hydrated), clear the stale expand state so the next click expands + loads.
  useEffect(() => {
    if (item.childrens.length === 0) return;
    if (loadingNodes.has(nodeKey)) return;
    if (!expandedNodes.has(nodeKey)) return;
    const allLoaded = item.childrens.every((id: string) => !!tree.items[id]);
    if (!allLoaded) {
      useEditorStore.getState().setNodeExpanded(nodeKey, false);
    }
  }, [nodeKey, item.childrens, tree.items, expandedNodes, loadingNodes]);

  const { handleToggleExpand } = useNodeExpand({
    item, variant, currentSensitivity, patchTree,
  });

  const getBorderCss = (): string | null => {
    if (hasSensitivityModified) return "1px solid #F7C325";
    if (selected && selected.id === item.id && (item.kind === "Input" || item.kind === "Output")) return "1px solid black";
    return null;
  };

  if (
    !isMatchSearchString(item, tree, searchStrings[variant]) ||
    (currentSensitivity && currentSensitivity !== "null" && showDiffToggled && !isModifiedInSensitivity(item, sensitivityModifiedIds))
  ) {
    return <div></div>;
  }

  const borderCss = getBorderCss();

  return (
    <div
      data-item-id={item.id}
      className={cn("rounded transition-shadow hover:shadow-md", borderCss && "border")}
      style={{ backgroundColor, border: borderCss || undefined }}
      onMouseDown={() => {
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
          active.blur();
        }
      }}
      onClick={(e) => {
        if (item.kind === "Input") setSelected(item);
        e.stopPropagation();
      }}
    >
      <div className="datagroup-component">
        <SnackBar
          content={contextSnackBarContent}
          onClose={() => setContextSnackBarContent({ open: false, severity: "success", message: "" })}
        />

        <div className="flex flex-row h-[44px] px-3 items-center">
          {editing ? (
            <FillNameForm item={item} tree={tree} setEditing={setEditing} reloadState={reloadState} currentState={currentState} patchTree={patchTree} />
          ) : (
            <div className="flex-1 min-w-0 flex items-center" style={{ color: shouldBeConsidered(item, tree, currentState) ? "black" : "grey" }}>
              <span className="truncate block">{item.name}</span>
              {computeErrors && <span className="material-symbols-outlined text-[20px] text-red-600 ml-1">warning_amber</span>}
            </div>
          )}

          {!editing && (
            <TreeItemActions
              item={item} tree={tree} variant={variant} level={level} scenarioId={scenario_id}
              currentState={currentState} reloadState={reloadState} userRights={userRights}
              setSelected={setSelected} setEditing={setEditing}
              setContextSnackBarContent={setContextSnackBarContent}
              allowedTypes={effectiveAllowedTypes}
            />
          )}

          {item.sensitivityModelData && item.isInconsiderable && (
            <div className="flex ml-2">
              <input
                type="checkbox"
                disabled={!parentConsidered(item, tree)}
                className="h-5 w-5 cursor-pointer accent-black disabled:opacity-50 disabled:cursor-not-allowed"
                onChange={() => {
                  item.sensitivityModelData!.to_consider = !item.sensitivityModelData!.to_consider;
                  apiService.putSensitivityModelData(item.sensitivityModelData!.id, item.sensitivityModelData!.to_consider);
                  currentSensitivity.is_computed = false;
                  reloadState();
                }}
                checked={parentConsidered(item, tree) ? item.sensitivityModelData.to_consider : false}
              />
            </div>
          )}

          {visibleChildrens.length ? (
            <button className="p-0.5 hover:bg-black/10 rounded ml-2" onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}>
              <span className={cn("material-symbols-outlined text-[24px] transition-transform", isExpanded && "rotate-180")}>expand_more</span>
            </button>
          ) : null}

          <DeleteModal item={item} removeElemFromTree={removeElemFromTree} reloadState={reloadState} recreateExcelInputIfSync={() => {}} currentState={currentState} patchTree={patchTree} setSelected={setSelected} />
        </div>
      </div>

      {selected && selected.id === item.id && renderInlineDetails && (
        <div className="mt-2 mb-1">{renderInlineDetails(item)}</div>
      )}

      {(visibleChildrens.length > 0 || loadingNodes.has(nodeKey)) && isExpanded && (
        <div className="mt-1" key={visibleChildrens.length}>
          {loadingNodes.has(nodeKey) && (
            <div className="py-2 pl-8 text-sm text-gray-400 flex items-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              Loading...
            </div>
          )}
          <Droppable droppableId={item.id} type={item.id}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {visibleChildrens.map((id, index) => (
                  <Draggable key={id} draggableId={id} index={index} type={item.id}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                        {tree.items[id] && (
                          <FDraggableDataGroup
                            item={tree.items[id]} currentSensitivity={currentSensitivity}
                            variant={variant} level={level + 1} scenario_id={scenario_id}
                            showDiffToggled={showDiffToggled} searchStrings={searchStrings}
                            selected={selected} setSelected={setSelected}
                            renderInlineDetails={renderInlineDetails}
                            sensitivityModifiedIds={sensitivityModifiedIds}
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </div>
  );
}

const DraggableDataGroup = memo(function DraggableDataGroup({
  item, currentSensitivity, variant, level, scenario_id, showDiffToggled,
  searchStrings, selected, setSelected, renderInlineDetails, sensitivityModifiedIds,
  allowedTypes,
}: DraggableDataGroupProps) {
  return (
    <FDraggableDataGroup
      item={item} currentSensitivity={currentSensitivity} variant={variant}
      level={level + 1} scenario_id={scenario_id} showDiffToggled={showDiffToggled}
      searchStrings={searchStrings} selected={selected} setSelected={setSelected}
      renderInlineDetails={renderInlineDetails} sensitivityModifiedIds={sensitivityModifiedIds}
      allowedTypes={allowedTypes}
    />
  );
});

export default DraggableDataGroup;
